import { getAssociatedTokenAddressSync } from '@solana/spl-token';
import { type Connection, PublicKey, type TransactionInstruction } from '@solana/web3.js';
import type BN from 'bn.js';
import {
  ASSOCIATED_TOKEN_PROGRAM_ID,
  MORFI_OPINION_PROGRAM_ID,
  SYSTEM_PROGRAM_ID,
  TOKEN_PROGRAM_ID,
} from '../constants/programs.js';
import { getProgram } from '../idl/loader.js';
import { derivePlatformFeeVaultPda } from '../pda/derivations.js';
import { invokeMethod } from './common.js';

/** Immutable market bindings supplied by an indexer or an already decoded Market. */
export interface BackingMarketAccounts {
  marketConfig: PublicKey;
  quoteMint: PublicKey;
  /** Bound timed schedule, or null for a conviction market. */
  eventSchedule: PublicKey | null;
}

interface BackingInstructionParams extends BackingMarketAccounts {
  backer: PublicKey;
  market: PublicKey;
  programId?: PublicKey;
}

export interface BackInstructionParams extends BackingInstructionParams {
  /** Principal deposited into prize liability (D6); backing fee is charged on top. */
  amount: BN;
}

export interface UnbackInstructionParams extends BackingInstructionParams {
  /** Shares burned (D6); unbacking fee is deducted from the quote payout. */
  burnAmount: BN;
}

/** Build only the canonical back instruction, without account reads or ATA preinstructions. */
export function buildBackInstruction(
  connection: Connection,
  params: BackInstructionParams,
): Promise<TransactionInstruction> {
  return buildBackingInstruction(connection, 'back', params, params.amount);
}

/** Build only the canonical unback instruction, without account reads or ATA preinstructions. */
export function buildUnbackInstruction(
  connection: Connection,
  params: UnbackInstructionParams,
): Promise<TransactionInstruction> {
  return buildBackingInstruction(connection, 'unback', params, params.burnAmount);
}

async function buildBackingInstruction(
  connection: Connection,
  side: 'back' | 'unback',
  params: BackingInstructionParams,
  amount: BN,
): Promise<TransactionInstruction> {
  const programId = params.programId ?? MORFI_OPINION_PROGRAM_ID;
  const program = await getProgram(connection, programId);
  const marketSeed = params.market.toBuffer();
  const shareMint = backingPda(programId, 'backer_share_mint', marketSeed);
  return invokeMethod(program, side, amount)
    .accounts({
      backer: params.backer,
      market: params.market,
      marketConfig: params.marketConfig,
      quoteMint: params.quoteMint,
      backerQuoteAccount: getAssociatedTokenAddressSync(params.quoteMint, params.backer, true),
      backerShareMint: shareMint,
      backerShareAccount: getAssociatedTokenAddressSync(shareMint, params.backer, true),
      marketQuoteVaultAuthority: backingPda(programId, 'market_quote_vault_authority', marketSeed),
      marketQuoteVault: backingPda(programId, 'market_quote_vault', marketSeed),
      platformFeeVault: derivePlatformFeeVaultPda(params.marketConfig, programId)[0],
      systemProgram: SYSTEM_PROGRAM_ID,
      tokenProgram: TOKEN_PROGRAM_ID,
      associatedTokenProgram: ASSOCIATED_TOKEN_PROGRAM_ID,
      eventSchedule: params.eventSchedule?.equals(PublicKey.default) ? null : params.eventSchedule,
      eventAuthority: backingPda(programId, '__event_authority'),
      program: programId,
    })
    .instruction();
}

export function backingPda(programId: PublicKey, seed: string, ...seeds: Buffer[]): PublicKey {
  return PublicKey.findProgramAddressSync([Buffer.from(seed), ...seeds], programId)[0];
}
